import { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, deleteDoc, updateDoc, query, orderBy, addDoc, serverTimestamp, writeBatch, getDoc, getDocs, where, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { Search, Download, Trash2, Mail, Loader2, AlertCircle, Plus, X, MessageCircle } from 'lucide-react';

export default function AdminStudents() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDeleting, setIsDeleting] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersDict, setUsersDict] = useState({});

  useEffect(() => {
    const fetchUsersMap = async () => {
      try {
        const snap = await getDocs(collection(db, 'users'));
        const dict = {};
        snap.forEach(d => { dict[d.id] = d.data() });
        setUsersDict(dict);
      } catch (err) { }
    };
    fetchUsersMap();
  }, []);

  // Toggle Single User
  const toggleUser = (id) => {
    setSelectedUsers(prev => prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]);
  };

  // Toggle All Users
  const toggleAllUsers = () => {
    if (selectedUsers.length === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  // Handle Delete Selected
  const handleDeleteSelected = async () => {
    if (selectedUsers.length === 0) return;
    if (!window.confirm(`هل أنت متأكد من رغبتك في حذف ${selectedUsers.length} مسجل تحديداً؟ لا يمكن التراجع.`)) return;

    setIsDeleting('bulk');
    try {
      const chunks = [];
      for (let i = 0; i < selectedUsers.length; i += 500) {
        chunks.push(selectedUsers.slice(i, i + 500));
      }

      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach(id => {
          batch.delete(doc(db, 'registrations', id));
        });
        await batch.commit();
      }

      setSelectedUsers([]);
    } catch (error) {
      console.error("Error bulk deleting:", error);
      alert('حدث خطأ أثناء الحذف.');
    } finally {
      setIsDeleting(null);
    }
  };

  // Filters State
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLevel, setFilterLevel] = useState('all');
  const [filterDate, setFilterDate] = useState('');

  // Notes State
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [currentNoteUser, setCurrentNoteUser] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  // Add Client State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    whatsapp: '',
    email: '',
    level: 'beginner',
    amountPaid: 0,
  });

  useEffect(() => {
    const q = query(collection(db, 'registrations'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const usersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        dateStr: doc.data().createdAt
          ? doc.data().createdAt.toDate().toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })
          : 'غير معروف'
      }));
      setUsers(usersData);
      setFilteredUsers(usersData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching users:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Search and Filter Logic
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    const filtered = users.filter(user => {
      const matchesSearch = (user.name && user.name.toLowerCase().includes(term)) || (user.whatsapp && user.whatsapp.includes(term));

      const currentStatus = user.status || 'interested';
      const matchesStatus = filterStatus === 'all' || currentStatus === filterStatus;

      const currentLevel = user.level || 'beginner';
      const matchesLevel = filterLevel === 'all' || currentLevel === filterLevel;

      let matchesDate = true;
      if (filterDate) {
        if (user.createdAt && user.createdAt.toDate) {
          const userDate = user.createdAt.toDate();
          const filterParts = filterDate.split('-');
          const fy = parseInt(filterParts[0], 10);
          const fm = parseInt(filterParts[1], 10) - 1;
          const fd = parseInt(filterParts[2], 10);

          if (userDate.getFullYear() !== fy || userDate.getMonth() !== fm || userDate.getDate() !== fd) {
            matchesDate = false;
          }
        } else {
          matchesDate = false;
        }
      }

      return matchesSearch && matchesStatus && matchesLevel && matchesDate;
    });
    setFilteredUsers(filtered);
  }, [searchTerm, filterStatus, filterLevel, filterDate, users]);

  // Handle Status Change
  const handleStatusChange = async (user, newStatus) => {
    try {
      await updateDoc(doc(db, 'registrations', user.id), { status: newStatus });
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: newStatus } : u));
      setFilteredUsers(prev => prev.map(u => u.id === user.id ? { ...u, status: newStatus } : u));

      // If changed to customer, add to enrollments collection if they signed up for a specific course
      if (newStatus === 'customer' && user.courseId && user.userId) {
        // Check if already enrolled
        const enrollQ = query(
          collection(db, 'enrollments'),
          where('userId', '==', user.userId),
          where('courseId', '==', user.courseId)
        );
        const enrollSnap = await getDocs(enrollQ);
        if (enrollSnap.empty) {
          // 1. Add to enrollments with actualPaidAmount
          await addDoc(collection(db, 'enrollments'), {
            userId: user.userId,
            courseId: user.courseId,
            courseTitle: user.courseTitle || 'دورة غير محددة',
            actualPaidAmount: user.amountPaid || 0,
            enrolledAt: serverTimestamp(),
          });
          // 2. Increment course students count
          await updateDoc(doc(db, 'courses', user.courseId), { studentsCount: increment(1) });
        }
      }

    } catch (error) {
      console.error("Error updating status:", error);
      alert('حدث خطأ أثناء تعديل التصنيف.');
    }
  };

  // Handle Amount Change
  const handleAmountChange = async (user, amount) => {
    const numAmount = parseFloat(amount) || 0;
    try {
      await updateDoc(doc(db, 'registrations', user.id), { amountPaid: numAmount });

      // Update enrollment actualPaidAmount if it exists
      if (user.courseId && user.userId) {
        const enrollQ = query(
          collection(db, 'enrollments'),
          where('userId', '==', user.userId),
          where('courseId', '==', user.courseId)
        );
        const enrollSnap = await getDocs(enrollQ);
        if (!enrollSnap.empty) {
          const enrollDoc = enrollSnap.docs[0];
          await updateDoc(doc(db, 'enrollments', enrollDoc.id), { actualPaidAmount: numAmount });
        }
      }

      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, amountPaid: numAmount } : u));
      setFilteredUsers(prev => prev.map(u => u.id === user.id ? { ...u, amountPaid: numAmount } : u));
    } catch (error) {
      console.error("Error updating amount:", error);
      alert('حدث خطأ أثناء حفظ المبلغ.');
    }
  };

  // Handle Delete
  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في حذف هذا المسجل نهائياً؟ لا يمكن التراجع عن هذا الإجراء.')) return;

    setIsDeleting(id);
    try {
      await deleteDoc(doc(db, 'registrations', id));
      // Remove from UI without refetching
      setUsers(prev => prev.filter(user => user.id !== id));
    } catch (error) {
      console.error("Error deleting document:", error);
      alert('حدث خطأ أثناء الحذف.');
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle Add Client
  const handleAddClient = async (e) => {
    e.preventDefault();
    if (!newClient.name || !newClient.whatsapp) {
      alert('الرجاء إدخال الاسم ورقم الواتساب');
      return;
    }

    setIsAdding(true);
    try {
      await addDoc(collection(db, 'registrations'), {
        name: newClient.name,
        whatsapp: newClient.whatsapp,
        email: newClient.email || '',
        level: newClient.level,
        status: 'customer',
        amountPaid: parseFloat(newClient.amountPaid) || 0,
        createdAt: serverTimestamp()
      });

      setIsAddModalOpen(false);
      setNewClient({
        name: '',
        whatsapp: '',
        email: '',
        level: 'beginner',
        amountPaid: 0,
      });

    } catch (error) {
      console.error("Error adding client:", error);
      alert('حدث خطأ أثناء إضافة العميل.');
    } finally {
      setIsAdding(false);
    }
  };

  // Handle Save Note
  const handleSaveNote = async () => {
    if (!currentNoteUser) return;
    setIsSavingNote(true);
    try {
      await updateDoc(doc(db, 'registrations', currentNoteUser.id), { notes: noteText });
      setUsers(prev => prev.map(user => user.id === currentNoteUser.id ? { ...user, notes: noteText } : user));
      setFilteredUsers(prev => prev.map(user => user.id === currentNoteUser.id ? { ...user, notes: noteText } : user));
      setIsNotesModalOpen(false);
    } catch (error) {
      console.error("Error updating notes:", error);
      alert('حدث خطأ أثناء حفظ الملاحظات.');
    } finally {
      setIsSavingNote(false);
      setCurrentNoteUser(null);
    }
  };

  // Export to CSV Logic
  const handleExportCSV = () => {
    if (users.length === 0) return;

    // CSV Headers
    const headers = ['الاسم', 'البريد الإلكتروني', 'رقم الواتساب', 'المستوى', 'التصنيف', 'المبلغ ($)', 'الملاحظات', 'تاريخ التسجيل'];

    // Map Data
    const csvRows = users.map(user => {
      const levelAr = user.level === 'beginner' ? 'مبتدئ' : user.level === 'intermediate' ? 'متوسط' : user.level === 'advanced' ? 'متقدم' : user.level;
      const statusAr = user.status === 'customer' ? 'عميل' : user.status === 'contacted' ? 'تم التواصل معه' : 'مهتم';
      const amount = user.status === 'customer' ? (user.amountPaid || 0) : 0;
      const notesCleaned = user.notes ? user.notes.replace(/"/g, '""') : '';
      const whatsappCleaned = user.whatsapp ? `\u200E${user.whatsapp}` : '';
      // Wrap values in quotes to handle commas and RTL characters cleanly
      return `"${user.name}","${user.email || ''}","${whatsappCleaned}","${levelAr}","${statusAr}","${amount}","${notesCleaned}","${user.dateStr}"`;
    });

    // Combine headers and rows
    const csvContent = [headers.join(','), ...csvRows].join('\n');

    // Add BOM for UTF-8 Excel support
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `عملاء_لباب_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">قاعدة الطلاب (المسجلين)</h1>
          <p className="text-slate-500 dark:text-zinc-400">إدارة تفاصيل المسجلين في الدورة، البحث، وتصدير البيانات.</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm"
          >
            <Plus className="w-5 h-5" />
            <span>إضافة طالب</span>
          </button>

          <button
            onClick={handleExportCSV}
            disabled={users.length === 0}
            className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900 text-white px-5 py-2.5 rounded-xl font-bold transition-all disabled:opacity-50 shadow-sm"
          >
            <Download className="w-5 h-5" />
            <span>تصدير CSV</span>
          </button>

          {selectedUsers.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              disabled={isDeleting === 'bulk'}
              className="flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20 px-5 py-2.5 rounded-xl font-bold transition-all shadow-sm border border-red-200 dark:border-red-500/30"
            >
              {isDeleting === 'bulk' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
              <span>حذف المحدد ({selectedUsers.length})</span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl overflow-hidden shadow-sm">

        {/* Toolbar & Filters */}
        <div className="p-4 md:p-6 border-b border-slate-200 dark:border-white/10 flex flex-col gap-4 bg-slate-50/50 dark:bg-zinc-900/50">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:max-w-md">
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                <Search className="w-5 h-5 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="ابحث بالاسم أو رقم الواتساب..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-11 py-3 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all text-slate-900 dark:text-white"
              />
            </div>
            <div className="text-lg font-bold text-slate-500 dark:text-zinc-400 whitespace-nowrap">
              إجمالي النتائج: <span className="text-primary-600 dark:text-primary-400">{filteredUsers.length}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            >
              <option value="all">كل التصنيفات</option>
              <option value="interested">مهتم</option>
              <option value="contacted">تم التواصل معه</option>
              <option value="customer">عميل</option>
            </select>

            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            >
              <option value="all">كل المستويات</option>
              <option value="beginner">مبتدئ</option>
              <option value="intermediate">متوسط</option>
              <option value="advanced">متقدم</option>
            </select>

            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="w-full sm:w-auto px-4 py-2 bg-white dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 transition-all text-slate-900 dark:text-white font-bold text-md"
            />

            {(filterStatus !== 'all' || filterLevel !== 'all' || filterDate !== '') && (
              <button
                onClick={() => { setFilterStatus('all'); setFilterLevel('all'); setFilterDate(''); }}
                className="text-mdfont-bold text-slate-400 hover:text-red-500 transition-colors mr-auto"
              >
                مسح الفلاتر
              </button>
            )}
          </div>
        </div>

        {/* Data Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600 mb-4" />
              <p className="font-bold">جاري تحميل البيانات...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <AlertCircle className="w-12 h-12 text-slate-300 dark:text-zinc-700 mb-4" />
              <p className="text-lg font-bold">لا يوجد طلاب يطابقون بحثك</p>
            </div>
          ) : (
            <table className="w-full min-w-[1200px] text-right border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-zinc-900 border-b border-slate-200 dark:border-white/10 text-slate-500 dark:text-zinc-400 text-lg font-bold">
                  <th className="px-4 py-4 whitespace-nowrap w-10">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                      onChange={toggleAllUsers}
                      className="w-4 h-4 rounded text-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-4 whitespace-nowrap">الاسم</th>
                  <th className="px-6 py-4 whitespace-nowrap">الدورة</th>
                  <th className="px-6 py-4 whitespace-nowrap">البريد الإلكتروني</th>
                  <th className="px-6 py-4 whitespace-nowrap">رقم الواتساب</th>
                  <th className="px-6 py-4 whitespace-nowrap">المستوى</th>
                  <th className="px-6 py-4 whitespace-nowrap">التصنيف</th>
                  <th className="px-6 py-4 whitespace-nowrap">المبلغ ($)</th>
                  <th className="px-6 py-4 whitespace-nowrap">تاريخ التسجيل</th>
                  <th className="px-6 py-4 min-w-[150px]">الملاحظات</th>
                  <th className="px-6 py-4 whitespace-nowrap text-center">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {filteredUsers.map((rawUser) => {
                  const userWhatsapp = rawUser.whatsapp || usersDict[rawUser.userId]?.phone || usersDict[rawUser.userId]?.whatsapp || '';
                  const userLevel = rawUser.level || usersDict[rawUser.userId]?.designLevel || 'beginner';
                  const user = { ...rawUser, whatsapp: userWhatsapp, level: userLevel };

                  const isPaid = user.amountPaid > 0;
                  return (
                    <tr key={user.id} className={`transition-colors group ${selectedUsers.includes(user.id)
                        ? 'bg-blue-50/50 dark:bg-blue-500/5'
                        : isPaid
                          ? 'bg-emerald-50/30 hover:bg-emerald-50/70 dark:bg-emerald-500/5 dark:hover:bg-emerald-500/10'
                          : 'hover:bg-slate-50/50 dark:hover:bg-white/[0.02]'
                      }`}>
                      <td className="px-4 py-5" onClick={() => toggleUser(user.id)}>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => { }}
                          className="w-4 h-4 rounded text-blue-500 cursor-pointer pointer-events-none"
                        />
                      </td>
                      <td className="px-6 py-5 font-bold text-slate-900 dark:text-white whitespace-nowrap cursor-pointer" onClick={() => toggleUser(user.id)}>
                        {user.name}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        {user.courseTitle ? (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-md font-bold bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400 max-w-[160px] truncate" title={user.courseTitle}>
                            {user.courseTitle}
                          </span>
                        ) : (
                          <span className="text-slate-400 dark:text-zinc-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        {user.email ? (
                          <a href={`mailto:${user.email}`} className="text-slate-600 dark:text-zinc-300 hover:text-blue-600 font-medium font-sans" dir="ltr">
                            {user.email}
                          </a>
                        ) : (
                          <span className="text-slate-400 dark:text-zinc-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        {user.whatsapp ? (
                          <a
                            href={`https://wa.me/${user.whatsapp.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 text-slate-600 dark:text-zinc-300 hover:text-green-600 font-medium font-sans"
                            dir="ltr"
                          >
                            +{user.whatsapp.replace(/\D/g, '')}
                          </a>
                        ) : (
                          <span className="text-slate-400 dark:text-zinc-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-md font-bold ${user.level === 'beginner'
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400'
                          : 'bg-purple-50 text-purple-600 dark:bg-purple-500/10 dark:text-purple-400'
                          }`}>
                          {user.level === 'beginner' ? 'مبتدئ' : user.level === 'intermediate' ? 'متوسط' : user.level === 'advanced' ? 'متقدم' : user.level}
                        </span>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <select
                          value={user.status || 'interested'}
                          onChange={(e) => handleStatusChange(user, e.target.value)}
                          className={`text-md font-bold rounded-full px-3 py-1 border outline-none transition-colors cursor-pointer appearance-none ${(!user.status || user.status === 'interested')
                            ? 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20'
                            : user.status === 'contacted'
                              ? 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20'
                              : 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20'
                            }`}
                          style={{ textAlignLast: 'center' }}
                        >
                          <option value="interested" className="text-slate-900 bg-white font-sans font-bold">مهتم</option>
                          <option value="contacted" className="text-slate-900 bg-white font-sans font-bold">تم التواصل معه</option>
                          <option value="customer" className="text-slate-900 bg-white font-sans font-bold">عميل</option>
                        </select>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        {user.status === 'customer' ? (
                          <div className="relative w-24">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold">$</span>
                            <input
                              type="number"
                              defaultValue={user.amountPaid || 0}
                              onBlur={(e) => handleAmountChange(user, e.target.value)}
                              className="w-full pl-8 pr-3 py-1.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-lg outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 transition-all font-bold text-slate-900 dark:text-white"
                              min="0"
                              dir="ltr"
                            />
                          </div>
                        ) : (
                          <span className="text-slate-400 dark:text-zinc-500 font-bold">-</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-lg text-slate-500 dark:text-zinc-400 font-medium whitespace-nowrap">
                        {user.dateStr}
                      </td>
                      <td className="px-6 py-5">
                        <div
                          onClick={() => {
                            setCurrentNoteUser(user);
                            setNoteText(user.notes || '');
                            setIsNotesModalOpen(true);
                          }}
                          className={`cursor-pointer max-w-[150px] truncate ${user.notes ? 'text-slate-700 dark:text-zinc-300 font-bold' : 'text-slate-400 dark:text-zinc-500 text-mditalic'}`}
                          title="انقر لتعديل الملاحظات"
                        >
                          {user.notes || 'أضف ملاحظة...'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center gap-2 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
                          {user.whatsapp && (
                            <a
                              href={`https://wa.me/${user.whatsapp.replace(/\D/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 text-slate-400 hover:text-emerald-600 bg-slate-100 hover:bg-emerald-50 dark:bg-white/5 dark:hover:bg-emerald-500/10 rounded-lg transition-colors"
                              title="مراسلة واتساب"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => handleDelete(user.id)}
                            disabled={isDeleting === user.id}
                            className="p-2 text-slate-400 hover:text-red-600 bg-slate-100 hover:bg-red-50 dark:bg-white/5 dark:hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                            title="حذف السجل"
                          >
                            {isDeleting === user.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      </div>

      {/* Add Client Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-zinc-800/50">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">إضافة طالب جديد</h2>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleAddClient} className="p-6 space-y-4">
              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">الاسم الكامل <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  placeholder="محمد أحمد"
                />
              </div>

              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">البريد الإلكتروني</label>
                <input
                  type="email"
                  value={newClient.email || ''}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  placeholder="example@gmail.com"
                  dir="ltr"
                  style={{ textAlign: 'left' }}
                />
              </div>

              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">رقم الواتساب <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  value={newClient.whatsapp}
                  onChange={(e) => setNewClient({ ...newClient, whatsapp: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  placeholder="201xxxxxxxxx"
                  dir="ltr"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">المستوى</label>
                  <select
                    value={newClient.level}
                    onChange={(e) => setNewClient({ ...newClient, level: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                  >
                    <option value="beginner">مبتدئ</option>
                    <option value="intermediate">متوسط</option>
                    <option value="advanced">متقدم</option>
                  </select>
                </div>

                <div>
                  <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-1">المبلغ المدفوع ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={newClient.amountPaid}
                    onChange={(e) => setNewClient({ ...newClient, amountPaid: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-bold text-slate-900 dark:text-white"
                    dir="ltr"
                  />
                </div>
              </div>

              <div className="pt-4 flex gap-3">
                <button
                  type="submit"
                  disabled={isAdding}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-bold transition-all disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {isAdding ? <Loader2 className="w-5 h-5 animate-spin" /> : 'إضافة الطالب'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-900 dark:text-white py-3 rounded-xl font-bold transition-all"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {isNotesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-zinc-900 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl relative animate-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-white/10 flex justify-between items-center bg-slate-50 dark:bg-zinc-800/50">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">ملاحظات: {currentNoteUser?.name}</h2>
              <button
                onClick={() => setIsNotesModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-mdfont-bold text-slate-700 dark:text-zinc-300 mb-2">اكتب ملاحظاتك هنا</label>
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows="5"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-black/50 border border-slate-200 dark:border-white/10 rounded-xl outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all font-medium text-slate-900 dark:text-white resize-none"
                  placeholder="أضف أي تفاصيل أو ملاحظات حول مستوى العميل، طلباته، إلخ..."
                ></textarea>
              </div>

              <div className="pt-2 flex gap-3">
                <button
                  onClick={handleSaveNote}
                  disabled={isSavingNote}
                  className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-xl font-bold transition-all disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {isSavingNote ? <Loader2 className="w-5 h-5 animate-spin" /> : 'حفظ الملاحظات'}
                </button>
                <button
                  onClick={() => setIsNotesModalOpen(false)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-900 dark:text-white py-3 rounded-xl font-bold transition-all"
                >
                  إلغاء
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
